// Harmony: progression bank, chord qualities, rootless voicings with voice leading, bass notes, keys and scales.

/** Chord qualities as interval sets (semitones above the root). */
export const QUALITIES = {
  maj7: [0, 4, 7, 11], maj9: [0, 4, 7, 11, 14], '6': [0, 4, 7, 9], '69': [0, 4, 7, 9, 14],
  m7: [0, 3, 7, 10], m9: [0, 3, 7, 10, 14], m6: [0, 3, 7, 9], m7b5: [0, 3, 6, 10],
  '7': [0, 4, 7, 10], '9': [0, 4, 7, 10, 14], '13': [0, 4, 10, 14, 21],
  '7b9': [0, 4, 7, 10, 13], '7s9': [0, 4, 7, 10, 15], '7sus4': [0, 5, 7, 10],
  // the styles' (§12)
  add9: [0, 4, 7, 14], madd9: [0, 3, 7, 14], m11: [0, 3, 7, 10, 14, 17], '9sus4': [0, 5, 7, 10, 14],
  maj7sus2: [0, 2, 7, 11, 14], maj9s11: [0, 4, 7, 11, 14, 18], dim7: [0, 3, 6, 9],
};

/** 4-bar progressions: [semitonesFromTonic, quality, beats]. Two chords in a bar = 2 + 2 beats. */
export const PROGRESSIONS = {
  major: {
    M1: [[2, 'm9', 4], [7, '13', 4], [0, 'maj9', 4], [0, 'maj9', 4]],
    M2: [[0, 'maj7', 4], [9, 'm9', 4], [2, 'm9', 4], [7, '13', 4]],
    M3: [[5, 'maj7', 4], [4, 'm7', 4], [2, 'm9', 4], [0, 'maj9', 4]],
    M4: [[5, 'maj7', 4], [5, 'm9', 4], [4, 'm7', 4], [9, 'm9', 4]],
    M5: [[5, 'maj9', 4], [4, '7b9', 4], [9, 'm9', 4], [7, 'm9', 2], [0, '13', 2]],
    M6: [[0, 'maj9', 4], [0, 'maj9', 4], [5, 'maj9', 4], [5, 'maj9', 4]],
    M7: [[0, 'maj9', 4], [4, 'm7', 4], [5, 'maj7', 4], [5, 'm6', 4]],
    M8: [[5, 'maj7', 4], [10, '9', 4], [0, 'maj9', 4], [0, 'maj9', 4]],               // IVmaj7 · ♭VII9 · Imaj9 (backdoor)
    M9: [[2, 'm9', 4], [4, 'm7', 4], [5, 'maj9', 4], [7, '9sus4', 4]],                // ii9 · iii7 · IVmaj9 · V9sus4
    M10: [[0, 'maj9', 4], [4, '7b9', 4], [5, 'maj9', 4], [5, 'm6', 4]],               // Imaj9 · III7♭9 · IVmaj9 · iv6
    M11: [[0, 'maj9', 4], [8, 'maj9', 4], [5, 'maj9', 4], [10, '13', 4]],             // Imaj9 · ♭VImaj9 · IVmaj9 · ♭VII13
    M12: [[0, 'maj9', 4], [9, 'm9', 4], [5, 'maj7', 4], [7, '9sus4', 4]],             // Imaj9 · vi9 · IVmaj7 · V9sus4
    M13: [[2, 'm9', 4], [7, '13', 4], [0, 'maj9', 4], [9, '7b9', 4]],                 // ii9 · V13 · Imaj9 · VI7♭9
    M14: [[4, 'm7', 4], [9, 'm9', 4], [2, 'm9', 4], [7, '13', 4]],                    // iii7 · vi9 · ii9 · V13
  },
  minor: {
    m1: [[0, 'm9', 4], [5, 'm9', 4], [10, '13', 4], [3, 'maj9', 4]],
    m2: [[0, 'm9', 4], [0, 'm9', 4], [5, '9', 4], [5, '9', 4]],
    m3: [[8, 'maj9', 4], [10, '6', 4], [0, 'm9', 4], [0, 'm9', 4]],
    m4: [[2, 'm7b5', 4], [7, '7b9', 4], [0, 'm9', 4], [0, 'm9', 4]],
    m5: [[0, 'm9', 4], [3, 'maj7', 4], [8, 'maj7', 4], [7, '7s9', 4]],
    m6: [[0, 'm9', 4], [8, 'maj7', 4], [5, 'm9', 4], [7, '7sus4', 2], [7, '7b9', 2]],
    m7: [[0, 'm9', 4], [10, '6', 4], [8, 'maj9', 4], [7, '7s9', 4]],                  // i9 · ♭VII6 · ♭VImaj9 · V7♯9
    m8: [[0, 'm9', 4], [5, '9', 4], [7, 'm9', 4], [0, 'm11', 4]],                     // i9 · IV9 · v9 · i11 (Dorian)
    m9: [[8, 'maj9', 4], [7, '7b9', 4], [0, 'm9', 4], [0, 'm11', 4]],                 // ♭VImaj9 · V7♭9 · i9 · i11
    m10: [[0, 'm9', 4], [0, 'm7', 4], [0, 'm6', 4], [8, 'maj7', 4]],                  // i9 · i7 · i6 · ♭VImaj7 (line cliché)
    m11: [[0, 'm9', 4], [3, 'maj9', 4], [5, 'm9', 4], [10, '13', 4]],                 // i9 · ♭IIImaj9 · iv9 · ♭VII13
    m12: [[5, 'm9', 4], [10, '13', 4], [3, 'maj9', 4], [8, 'maj9', 4]],               // iv9 · ♭VII13 · ♭IIImaj9 · ♭VImaj9
  },
};
const TURNAROUND = { major: [[2, 'm9', 2], [7, '13', 2]], minor: [[2, 'm7b5', 2], [7, '7b9', 2]] };

export const NOTE_NAMES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
export const keyName = k => `${NOTE_NAMES[k.tonic]} ${k.mode}`;
const mod12 = n => ((n % 12) + 12) % 12;

/** Expand a stored progression into timed chords, optionally stretched (chordBars 2) and with a turnaround replacing
 *  its last bar: a mode ('major' | 'minor': the ii-V above) or a style's own bar of [root, quality, beats].
 *  → { chords: [{ root, q, start, beats }], beats } (root = semitones from tonic) */
export function expand(prog, { stretch = 1, turnaround = null } = {}) {
  let t = 0, chords = prog.map(([root, q, beats]) => { const c = { root, q, start: t, beats: beats * stretch }; t += c.beats; return c; });
  if (turnaround) {
    const cut = t - 4;
    chords = chords.filter(c => c.start < cut).map(c => ({ ...c, beats: Math.min(c.beats, cut - c.start) }));
    let s = cut;
    for (const [root, q, beats] of Array.isArray(turnaround) ? turnaround : TURNAROUND[turnaround]) { chords.push({ root, q, start: s, beats }); s += beats; }
  }
  return { chords, beats: t };
}

/** Chord pitch classes for a rootless voicing {3, 7, 9, 5 or 13}: drop the 5th first when there are more than
 *  4 tones, add the 9th when there are fewer (the ♭5 of m7♭5 is kept). → { root (pc), iv: intervals } */
export function spell(key, chord) {
  const root = mod12(key.tonic + chord.root);
  let iv = QUALITIES[chord.q].filter(i => i % 12 !== 0);
  if (iv.length > 4 && iv.includes(7)) iv = iv.filter(i => i !== 7);
  while (iv.length > 4) iv.pop();
  if (iv.length < 4 && !iv.some(i => i % 12 === 2)) iv.push(14);
  return { root, iv };
}

/** Candidate voicings: 4 close-position rotations plus their drop-2s, at every octave with the lowest note in
 *  MIDI 48-60 and the top <= 76; intervals under 3 semitones below MIDI 52 are rejected. */
export function candidates({ root, iv }) {
  const pcs = [...new Set(iv.map(i => i % 12))].sort((a, b) => a - b);
  const n = pcs.length, shapes = [];
  for (let k = 0; k < n; k++) {
    const close = [];
    for (let j = 0; j < n; j++) { let v = pcs[(k + j) % n]; while (close.length && v <= close[close.length - 1]) v += 12; close.push(v); }
    shapes.push(close);
    if (n >= 3) { const d2 = close.slice(); d2[n - 2] -= 12; shapes.push(d2.sort((a, b) => a - b)); }
  }
  const out = [];
  for (const s of shapes) for (let o = 1; o <= 6; o++) {
    const notes = s.map(i => root + i + 12 * o);
    if (notes[0] < 48 || notes[0] > 60 || notes[notes.length - 1] > 76) continue;
    let ok = true;
    for (let i = 1; i < notes.length; i++) if (notes[i] - notes[i - 1] < 3 && notes[i - 1] < 52) ok = false;
    if (ok) out.push(notes);
  }
  return out;
}

const mean = a => a.reduce((s, v) => s + v, 0) / a.length;
/** Voice-leading cost: Σ|Δ| over sorted voices + 2·max(0, |Δtop| − 4) + 0.5·|mean − 62|. The first chord (no prev)
 *  aims at 62 + register instead, the track's own register (plan.register). */
export function leadCost(a, prev, register = 0) {
  let c = 0.5 * Math.abs(mean(a) - 62 - (prev ? 0 : register));
  if (!prev) return c;
  const n = Math.min(a.length, prev.length);
  for (let i = 0; i < n; i++) c += Math.abs(a[i] - prev[i]);
  return c + 2 * Math.max(0, Math.abs(a[a.length - 1] - prev[prev.length - 1]) - 4);
}
/** Cheapest voicing of a spelled chord after `prev` (MIDI array or null for the first chord, voiced `register`
 *  semitones off the home register). */
export function voice(spelled, prev, register = 0) {
  const cands = candidates(spelled);
  if (!cands.length) return spelled.iv.map(i => 48 + mod12(spelled.root + i - 48) + (i >= 12 ? 12 : 0)).sort((a, b) => a - b);
  let best = cands[0], bc = Infinity;
  for (const c of cands) { const k = leadCost(c, prev, register); if (k < bc) { bc = k; best = c; } }
  return best;
}

/** Root at the octave nearest the previous bass note, within MIDI 33-47. */
export function bassNote(pc, prev = 40) {
  let best = 33 + mod12(pc - 33), bd = Infinity;
  for (let n = best; n <= 47; n += 12) { const d = Math.abs(n - prev); if (d < bd) { bd = d; best = n; } }
  return best;
}

export const PENTA = { major: [0, 2, 4, 7, 9], minor: [0, 3, 5, 7, 10] };
/** The key's pentatonic pitch classes. */
export const pentatonic = key => PENTA[key.mode].map(i => mod12(key.tonic + i));
/** MIDI notes of the key's pentatonic within [lo, hi]. */
export function scaleNotes(key, lo, hi) {
  const pcs = pentatonic(key), out = [];
  for (let m = lo; m <= hi; m++) if (pcs.includes(mod12(m))) out.push(m);
  return out;
}

/** Next track's key: same 15 %, a 4th or 5th away 40 %, relative major/minor 20 %, random 25 %. */
export function relatedKey(prev, r) {
  const u = r();
  if (u < 0.15) return { ...prev };
  if (u < 0.55) return { tonic: mod12(prev.tonic + (r() < 0.5 ? 5 : 7)), mode: prev.mode };
  if (u < 0.75) return prev.mode === 'major' ? { tonic: mod12(prev.tonic + 9), mode: 'minor' } : { tonic: mod12(prev.tonic + 3), mode: 'major' };
  return randomKey(r);
}
export const randomKey = r => ({ tonic: Math.floor(r() * 12), mode: r() < 0.55 ? 'minor' : 'major' });

/** Snap a frequency to the nearest note of the key's pentatonic (a C-minor-ish default without a key). */
export function snapToKey(hz, key) {
  const pcs = key ? pentatonic(key) : PENTA.minor;
  const m = 69 + 12 * Math.log2(hz / 440);
  let best = Math.round(m), bd = Infinity;
  for (let n = Math.floor(m) - 6; n <= Math.ceil(m) + 6; n++) {
    if (!pcs.includes(mod12(n))) continue;
    const d = Math.abs(n - m);
    if (d < bd) { bd = d; best = n; }
  }
  return 440 * Math.pow(2, (best - 69) / 12);
}

/* ---------------- development: a loop's other passes, the bridge, the key lift (docs/AUDIO_DESIGN.md §4) ---------------- */
const DOM = new Set(['7', '9', '13', '7b9', '7s9', '7sus4', '9sus4']);
/** 'M' a major third, 'm' a minor one, 's' neither (sus, power, quartal) */
const thirdOf = q => { const iv = QUALITIES[q] || []; return iv.includes(4) ? 'M' : iv.includes(3) ? 'm' : 's'; };
const flat5 = q => { const iv = QUALITIES[q] || []; return iv.includes(6) && !iv.includes(7); };
/** The qualities a style that varies its loops as jazz ('jazz') may play besides its own bank's. */
export const JAZZ_QUALITIES = ['maj7', 'maj9', '6', '69', 'm7', 'm9', 'm11', 'm6', 'm7b5', '7', '9', '13', '7b9', '7s9', '7sus4', '9sus4'];
/** Every quality a bank ({ major, minor } of stored progressions) plays. */
export const bankQualities = bank =>
  new Set(Object.values(bank).flatMap(m => Object.values(m || {}).flatMap(p => p.map(c => c[1]))));

const beatsOf = prog => prog.reduce((s, c) => s + c[2], 0);
const same = (a, b) => a.length === b.length && a.every((c, i) => c[0] === b[i][0] && c[1] === b[i][1] && c[2] === b[i][2]);
const pickOf = (r, list) => list[Math.floor(r() * list.length)];
/** a stored progression's chords that sound in beats [a, b), cut to it */
function slice(prog, a, b) {
  const out = [];
  let t = 0;
  for (const [root, q, beats] of prog) {
    const s = Math.max(t, a), e = Math.min(t + beats, b);
    if (e > s) out.push([root, q, e - s]);
    t += beats;
  }
  return out;
}

/**
 * Another pass of a stored progression, the loop a listener already knows changed in one place, or null when no move
 * applies. Every idiom may give one chord another colour (a quality of the same third and function from `allowed`) or
 * answer with a sibling's second half (`siblings`: the bank's progressions of its family); 'half' only answers (a style
 * whose every chord note matters to its scale, like a raga's). 'jazz' adds a jazz player's
 * moves: a ii–V back into the loop's first chord in its last bar (or the tritone sub of that V), a tritone sub of a
 * dominant resolving down a fifth, a secondary dominant on the last two beats before a new chord, a dominant split into
 * its ii–V, the borrowed iv, and the tonic's stand-ins (vi or iii in major, ♭III or ♭VI in minor). Moves are tried in
 * a weighted order, each drawn from `r`, so the same stream gives the same pass.
 * → { chords (stored, as many beats as `prog`), end: the last bar as expand()'s turnaround | null, move } | null
 */
export function vary(prog, { r, mode, allowed, idiom = 'colour', siblings = [] }) {
  const ok = q => allowed.has(q) && !!QUALITIES[q];
  const total = beatsOf(prog), n = prog.length;
  const nextOf = k => prog[(k + 1) % n];
  const moves = {
    colour: () => {
      const alts = q => [...allowed].filter(x => x !== q && ok(x) && thirdOf(x) === thirdOf(q) && DOM.has(x) === DOM.has(q) && flat5(x) === flat5(q));
      const ks = prog.map((c, k) => k).filter(k => alts(prog[k][1]).length);
      const later = ks.filter(k => k > 0), k = pickOf(r, later.length ? later : ks);
      if (k == null) return null;
      const out = prog.map(c => c.slice());
      out[k][1] = pickOf(r, alts(prog[k][1]));
      return { chords: out };
    },
    half: () => {
      if (total !== 16) return null;
      const sib = siblings.filter(p => beatsOf(p) === 16 && !same(slice(p, 8, 16), slice(prog, 8, 16)));
      const p = pickOf(r, sib);
      return p ? { chords: [...slice(prog, 0, 8), ...slice(p, 8, 16)] } : null;
    },
    end: () => {
      const [T, tq] = prog[0], minor = thirdOf(tq) === 'm';
      const ii = minor ? 'm7b5' : 'm9', V = minor ? '7b9' : '13';
      if (r() < 0.35 && ok('9')) return { chords: prog, end: [[(T + 1) % 12, '9', 4]] };          // ♭II7 into the loop
      return ok(ii) && ok(V) ? { chords: prog, end: [[(T + 2) % 12, ii, 2], [(T + 7) % 12, V, 2]] } : null;
    },
    tritone: () => {
      const ks = prog.map((c, k) => k).filter(k => DOM.has(prog[k][1]) && nextOf(k)[0] === (prog[k][0] + 5) % 12);
      const k = pickOf(r, ks), q = ok('13') && r() < 0.5 ? '13' : '9';
      if (k == null || !ok(q)) return null;
      const out = prog.map(c => c.slice());
      out[k] = [(prog[k][0] + 6) % 12, q, prog[k][2]];
      return { chords: out };
    },
    secdom: () => {
      const ks = prog.map((c, k) => k).filter(k => k < n - 1 && prog[k][2] === 4 && nextOf(k)[0] !== prog[k][0] && (nextOf(k)[0] + 7) % 12 !== prog[k][0]);
      const k = pickOf(r, ks);
      if (k == null) return null;
      const X = nextOf(k), V = thirdOf(X[1]) === 'm' ? '7b9' : '13';
      if (!ok(V)) return null;
      return { chords: [...prog.slice(0, k), [prog[k][0], prog[k][1], 2], [(X[0] + 7) % 12, V, 2], ...prog.slice(k + 1)] };
    },
    split: () => {
      const ks = prog.map((c, k) => k).filter(k => DOM.has(prog[k][1]) && prog[k][2] === 4 && prog[(k + n - 1) % n][0] !== (prog[k][0] + 7) % 12);
      const k = pickOf(r, ks);
      if (k == null) return null;
      const ii = thirdOf(nextOf(k)[1]) === 'm' ? 'm7b5' : 'm9';
      if (!ok(ii)) return null;
      return { chords: [...prog.slice(0, k), [(prog[k][0] + 7) % 12, ii, 2], [prog[k][0], prog[k][1], 2], ...prog.slice(k + 1)] };
    },
    borrowed: () => {
      const ks = prog.map((c, k) => k).filter(k => mode === 'major' && prog[k][0] === 5 && thirdOf(prog[k][1]) === 'M' && !DOM.has(prog[k][1]));
      const k = pickOf(r, ks), q = r() < 0.5 ? 'm6' : 'm9';
      if (k == null || !ok(q)) return null;
      const out = prog.map(c => c.slice());
      out[k][1] = q;
      return { chords: out };
    },
    diatonic: () => {
      const want = mode === 'major' ? 'M' : 'm';
      const ks = prog.map((c, k) => k).filter(k => k > 0 && prog[k][0] === 0 && thirdOf(prog[k][1]) === want && !DOM.has(prog[k][1]));
      const k = pickOf(r, ks), sub = mode === 'major' ? pickOf(r, [[9, 'm9'], [4, 'm7']]) : pickOf(r, [[3, 'maj9'], [8, 'maj9']]);
      if (k == null || !ok(sub[1])) return null;
      const out = prog.map(c => c.slice());
      out[k] = [sub[0], sub[1], prog[k][2]];
      return { chords: out };
    },
  };
  const w = idiom === 'jazz' ? { end: 3, tritone: 1.5, secdom: 1.5, split: 1, borrowed: 1, diatonic: 1, colour: 1, half: 0.6 }
    : idiom === 'half' ? { half: 1 } : { colour: 1, half: 1.2 };
  const left = { ...w };
  while (Object.keys(left).length) {
    let u = r() * Object.values(left).reduce((a, b) => a + b, 0), move = null;
    for (const [m, x] of Object.entries(left)) if ((u -= x) < 0) { move = m; break; }
    move = move || Object.keys(left)[0];
    delete left[move];
    const v = moves[move]();
    if (!v || beatsOf(v.chords) !== total || !v.chords.every(c => ok(c[1]))) continue;
    if (v.end && !v.end.every(c => ok(c[1]))) continue;
    if (!v.end && same(v.chords, prog)) continue;
    if (v.end && same(slice(prog, total - 4, total), v.end)) continue;
    return { chords: v.chords, end: v.end || null, move };
  }
  return null;
}

/** A key moved up `by` semitones (the lift: the last main section and the outro, §7). */
export const liftKey = (key, by) => (by ? { tonic: mod12(key.tonic + by), mode: key.mode } : key);
/** Every progression a track plays (its loops, their other passes, the bridge), for a style that renders its chords ahead. */
export const trackProgs = plan => [plan.progA, plan.progB, plan.progC, ...Object.values(plan.vars || {})].filter(Boolean);
/** Every chord a track plays, with the plan that voices it: [chord, plan] pairs over its loops, their passes and the
 *  bridge, then `fin` (the style's final chord), and again in the lifted key past a lift. For a style that renders its
 *  chords ahead of time, first chords first. */
export function playedChords(plan, fin) {
  const progs = trackProgs(plan), views = plan.lift ? [plan, { ...plan, key: liftKey(plan.key, plan.lift.by) }] : [plan];
  return views.flatMap(P => [...progs.flatMap(p => p.chords), fin].map(c => [c, P]));
}
